import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { resizeImageToBlob, previewUrlFor, generateSlots } from "@/lib/utils-uploads";
import { RefreshCcw, Truck, CheckCircle2, Upload, X, ChevronRight, Sparkles, ImageIcon } from "lucide-react";

const CATEGORY_OPTIONS = [
  { id: "phones", label: "Smartphone" },
  { id: "laptops", label: "Laptop / Tablet" },
  { id: "audio", label: "Audio (headphones / earbuds)" },
  { id: "wearables", label: "Smartwatch / Wearable" },
  { id: "tv", label: "Television" },
  { id: "gaming", label: "Gaming Console" },
  { id: "accessories", label: "Accessory" },
];

const CONDITIONS = [
  { id: "like_new", label: "Like new — no scratches, fully working" },
  { id: "good", label: "Good — minor wear, fully working" },
  { id: "fair", label: "Fair — visible scratches, working" },
  { id: "poor", label: "Poor / damaged — not fully working" },
];

const PHOTO_LABELS = ["Front", "Back", "Screen on", "Accessories", "Bill"];

export default function Buyback() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [catalog, setCatalog] = useState({ devices: {}, ages: [] });
  const slots = useMemo(() => generateSlots(3), []);

  const [form, setForm] = useState({
    category: "phones",
    brand: "",
    model: "",
    age_value: "1-2y",
    condition: "good",
    accessories: false,
    has_bill: false,
  });
  const [quote, setQuote] = useState(null);
  const [photos, setPhotos] = useState([]); // [{preview_url, storage_path, content_type, label, uploading}]
  const [pickup, setPickup] = useState({
    full_name: "",
    phone: "",
    line1: "",
    city: "",
    pincode: "",
    landmark: "",
  });
  const [slot, setSlot] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/catalog/buyback").then(({ data }) => {
      setCatalog(data);
    });
  }, []);

  const brands = Object.keys(catalog.devices?.[form.category] || {});
  const models = catalog.devices?.[form.category]?.[form.brand] || [];

  const setF = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: val };
      if (k === "category") { next.brand = ""; next.model = ""; }
      if (k === "brand") next.model = "";
      return next;
    });
  };
  const setP = (k) => (e) => setPickup((p) => ({ ...p, [k]: e.target.value }));

  const ageMonths = useMemo(() => {
    const a = (catalog.ages || []).find((x) => x.value === form.age_value);
    return a?.months ?? 12;
  }, [form.age_value, catalog.ages]);

  const getQuote = async () => {
    if (!form.brand || !form.model) { toast.error("Pick a brand & model"); return; }
    try {
      const { data } = await api.post("/buyback/quote", {
        category: form.category,
        brand: form.brand,
        model: form.model,
        age_months: ageMonths,
        condition: form.condition,
        accessories: form.accessories,
        has_bill: form.has_bill,
      });
      setQuote(data.quoted_price);
      setStep(2);
    } catch { toast.error("Could not fetch quote"); }
  };

  const onAddPhoto = async (e, index) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting same file
    if (!file) return;
    if (!user) { toast.error("Please sign in first"); navigate("/login"); return; }
    const label = PHOTO_LABELS[index] || `Photo ${index + 1}`;
    try {
      const { blob, contentType, filename } = await resizeImageToBlob(file);
      if (blob.size > 5 * 1024 * 1024) {
        toast.error("Image is too large — try a smaller photo");
        return;
      }
      const preview_url = previewUrlFor(blob);
      setPhotos((arr) => {
        const next = [...arr];
        next[index] = { preview_url, storage_path: null, content_type: contentType, label, uploading: true };
        return next;
      });
      const fd = new FormData();
      fd.append("file", blob, filename);
      const { data } = await api.post("/buyback/photos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos((arr) => {
        const next = [...arr];
        if (next[index]) {
          next[index] = { ...next[index], storage_path: data.storage_path, content_type: data.content_type, uploading: false };
        }
        return next;
      });
    } catch (err) {
      setPhotos((arr) => arr.map((p, i) => (i === index ? null : p)).filter(Boolean));
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    }
  };

  const removePhoto = (i) => {
    setPhotos((arr) => {
      const p = arr[i];
      if (p?.preview_url) URL.revokeObjectURL(p.preview_url);
      const next = [...arr];
      next[i] = null;
      return next.filter(Boolean);
    });
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in"); navigate("/login"); return; }
    if (!pickup.full_name || !pickup.phone || !pickup.line1 || !pickup.city || !pickup.pincode || !slot) {
      toast.error("Fill all pickup details and pick a slot"); return;
    }
    if (photos.some((p) => p && p.uploading)) {
      toast.error("Photos are still uploading, please wait"); return;
    }
    const uploadedPhotos = photos.filter((p) => p && p.storage_path);
    setSubmitting(true);
    try {
      await api.post("/buyback/requests", {
        category: form.category,
        brand: form.brand,
        model: form.model,
        age_months: ageMonths,
        condition: form.condition,
        accessories: form.accessories,
        has_bill: form.has_bill,
        quoted_price: quote,
        pickup_address: pickup,
        pickup_slot: slot,
        photos: uploadedPhotos.map((p) => ({ storage_path: p.storage_path, content_type: p.content_type })),
      });
      toast.success("Pickup scheduled!");
      navigate("/profile");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8" data-testid="buyback-page">
      <p className="vm-overline">Buyback / Resale</p>
      <h1 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
        Sell your old device.<br />
        Get <span className="text-volt-500">instant cash</span>.
      </h1>
      <p className="text-gray-600 mt-3 max-w-2xl">
        Pick your device, snap a few photos, schedule a free pickup. We pay on the spot.
      </p>

      {/* Stepper */}
      <div className="flex items-center gap-3 mt-6">
        {[
          { n: 1, l: "Device" },
          { n: 2, l: "Photos & pickup" },
        ].map((s, idx, arr) => (
          <React.Fragment key={s.n}>
            <div className={`flex items-center gap-2 ${step >= s.n ? "text-volt-500" : "text-gray-400"}`}>
              <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${
                step >= s.n ? "bg-volt-500 text-white" : "bg-gray-100 text-gray-500"
              }`}>{s.n}</div>
              <span className="text-sm font-semibold">{s.l}</span>
            </div>
            {idx < arr.length - 1 && <ChevronRight className="h-4 w-4 text-gray-400" />}
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-12 gap-5 mt-6">
        <div className="lg:col-span-7">
          {step === 1 && (
            <div className="vm-card p-6 lg:p-7" data-testid="buyback-step-1">
              <h2 className="font-display text-2xl font-bold">Tell us about your device</h2>
              <p className="text-sm text-gray-500 mt-1">All dropdowns — takes 30 seconds.</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <Field label="Device type">
                  <select className="vm-input" value={form.category} onChange={setF("category")} data-testid="buyback-category">
                    {CATEGORY_OPTIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
                <Field label="Brand">
                  <select className="vm-input" value={form.brand} onChange={setF("brand")} data-testid="buyback-brand">
                    <option value="">Select brand</option>
                    {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Model" className="sm:col-span-2">
                  <select className="vm-input" value={form.model} onChange={setF("model")} disabled={!form.brand} data-testid="buyback-model">
                    <option value="">{form.brand ? "Select model" : "Pick a brand first"}</option>
                    {models.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
                <Field label="How old is it?">
                  <select className="vm-input" value={form.age_value} onChange={setF("age_value")} data-testid="buyback-age">
                    {(catalog.ages || []).map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </Field>
                <Field label="Condition">
                  <select className="vm-input" value={form.condition} onChange={setF("condition")} data-testid="buyback-condition">
                    {CONDITIONS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-5">
                <CheckCard
                  checked={form.accessories}
                  onChange={() => setForm((f) => ({ ...f, accessories: !f.accessories }))}
                  title="Includes accessories"
                  subtitle="Charger, cable, box"
                  testId="buyback-accessories"
                />
                <CheckCard
                  checked={form.has_bill}
                  onChange={() => setForm((f) => ({ ...f, has_bill: !f.has_bill }))}
                  title="Original bill"
                  subtitle="Purchase invoice available"
                  testId="buyback-bill"
                />
              </div>

              <button
                className="vm-btn-primary mt-7 w-full sm:w-auto inline-flex items-center justify-center gap-2"
                onClick={getQuote}
                data-testid="buyback-get-quote"
              >
                Get instant quote <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {step === 2 && (
            <form onSubmit={submitRequest} className="vm-card p-6 lg:p-7" data-testid="buyback-step-2">
              <h2 className="font-display text-2xl font-bold">Add photos & pickup details</h2>
              <p className="text-sm text-gray-500 mt-1">Upload up to 5 photos for fast verification.</p>

              {/* Photo grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mt-5">
                {PHOTO_LABELS.map((label, i) => {
                  const p = photos[i];
                  return (
                    <label
                      key={label}
                      className="aspect-square rounded-xl border-2 border-dashed border-gray-300 bg-white hover:border-volt-500 hover:bg-volt-500/5 cursor-pointer flex flex-col items-center justify-center text-center p-2 relative overflow-hidden transition-colors"
                      data-testid={`buyback-photo-slot-${i}`}
                    >
                      {p ? (
                        <>
                          <img src={p.preview_url} alt={label} className="absolute inset-0 w-full h-full object-cover" />
                          {p.uploading && (
                            <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-10 text-white text-[10px] font-semibold">
                              Uploading…
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); removePhoto(i); }}
                            className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center z-10"
                            data-testid={`buyback-photo-remove-${i}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <span className="absolute bottom-1 left-1 right-1 text-[10px] font-semibold text-white bg-black/60 rounded px-1.5 py-0.5 z-0 truncate text-center">
                            {label}
                          </span>
                        </>
                      ) : (
                        <>
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                          <span className="text-[11px] font-semibold text-gray-600 mt-1">{label}</span>
                          <span className="text-[10px] text-gray-400">Tap to add</span>
                        </>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => onAddPhoto(e, i)}
                        data-testid={`buyback-photo-input-${i}`}
                      />
                    </label>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                <Upload className="h-3 w-3" /> Photos help us confirm the quote on pickup.
              </p>

              <div className="vm-divider my-6" />

              <h3 className="font-semibold text-sm">Pickup address</h3>
              <div className="grid sm:grid-cols-2 gap-4 mt-3">
                <Field label="Full name" className="sm:col-span-2">
                  <input className="vm-input" value={pickup.full_name} onChange={setP("full_name")} data-testid="buyback-name" />
                </Field>
                <Field label="Phone"><input className="vm-input" value={pickup.phone} onChange={setP("phone")} data-testid="buyback-phone" /></Field>
                <Field label="Pincode"><input className="vm-input" value={pickup.pincode} onChange={setP("pincode")} data-testid="buyback-pincode" /></Field>
                <Field label="Address" className="sm:col-span-2"><input className="vm-input" value={pickup.line1} onChange={setP("line1")} placeholder="House no, street, area" data-testid="buyback-address" /></Field>
                <Field label="City"><input className="vm-input" value={pickup.city} onChange={setP("city")} data-testid="buyback-city" /></Field>
                <Field label="Landmark (optional)"><input className="vm-input" value={pickup.landmark} onChange={setP("landmark")} data-testid="buyback-landmark" /></Field>
              </div>

              <h3 className="font-semibold text-sm mt-6">Pickup slot</h3>
              <select className="vm-input mt-2" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="buyback-slot">
                <option value="">Select a pickup slot</option>
                {slots.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>

              <div className="flex gap-3 mt-7">
                <button type="button" className="vm-btn-ghost" onClick={() => setStep(1)} data-testid="buyback-back">Back</button>
                <button type="submit" disabled={submitting} className="vm-btn-primary flex-1" data-testid="buyback-submit">
                  {submitting ? "Scheduling..." : "Schedule free pickup"}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Quote / Summary */}
        <div className="lg:col-span-5">
          <div className="vm-card p-6 lg:p-7 sticky top-24" data-testid="buyback-quote-card">
            <p className="vm-overline">Your quote</p>
            {quote != null ? (
              <>
                <p className="font-display text-5xl font-extrabold mt-3 text-volt-500">{inr(quote)}</p>
                <p className="text-xs text-gray-500 mt-2">Final price subject to physical inspection on pickup.</p>
                <div className="vm-divider my-5" />
                <SummaryRow k="Device" v={`${form.brand} ${form.model}`} />
                <SummaryRow k="Condition" v={CONDITIONS.find(c => c.id === form.condition)?.label.split("—")[0].trim()} />
                <SummaryRow k="Age" v={(catalog.ages || []).find(a => a.value === form.age_value)?.label} />
                {photos.length > 0 && <SummaryRow k="Photos" v={`${photos.length} added`} />}
              </>
            ) : (
              <>
                <p className="text-gray-500 mt-3 text-sm">Fill the form — your guaranteed cash quote appears here.</p>
                <div className="font-display text-5xl font-extrabold mt-3 text-gray-200">₹ ———</div>
              </>
            )}
            <div className="vm-divider my-5" />
            <div className="space-y-3 text-sm">
              <Perk icon={Sparkles} text="Quote in 30 seconds" />
              <Perk icon={Truck} text="Free doorstep pickup" />
              <Perk icon={CheckCircle2} text="Instant UPI / bank transfer" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="text-xs font-semibold text-gray-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function CheckCard({ checked, onChange, title, subtitle, testId }) {
  return (
    <button
      type="button"
      onClick={onChange}
      className={`text-left p-3 rounded-xl border transition-colors flex items-start gap-3 ${
        checked ? "border-volt-500 bg-volt-500/5" : "border-gray-200 bg-white hover:border-gray-300"
      }`}
      data-testid={testId}
    >
      <div className={`h-5 w-5 rounded-md border-2 flex items-center justify-center mt-0.5 shrink-0 ${
        checked ? "bg-volt-500 border-volt-500" : "border-gray-300 bg-white"
      }`}>
        {checked && <CheckCircle2 className="h-4 w-4 text-white" />}
      </div>
      <div className="leading-tight">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <p className="text-xs text-gray-500">{subtitle}</p>
      </div>
    </button>
  );
}

function SummaryRow({ k, v }) {
  return (
    <div className="flex justify-between text-sm py-1">
      <span className="text-gray-500">{k}</span>
      <span className="font-medium text-gray-900 text-right max-w-[60%] truncate">{v}</span>
    </div>
  );
}

function Perk({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 text-gray-700">
      <div className="h-7 w-7 rounded-lg bg-volt-500/10 flex items-center justify-center">
        <Icon className="h-3.5 w-3.5 text-volt-500" />
      </div>
      <span className="font-medium">{text}</span>
    </div>
  );
}
